import React, { Fragment } from 'react';
import { Box, Typography } from '@mui/material';
import {
    ScheduleHeadlineGridContainer,
    ScheduleIconGridContainer,
    ScheduleInfoGridContainer,
    ScheduleItemDesktopGridContainer,
    ScheduleItemMobileGridContainer
} from './ScheduleGridContainers';
import { MainContentIcon } from './MainContentIcon';
import { VirtualMeetingRoomLink } from './VirtualMeetingRoomLink';
import { formatDate, formatTime, isNextDay } from './helper/dateHelper';

const DisplayRoom = ({ room, begin, end }) => {
    if (!room) {
        return 'Raumbekanntgabe vor Ort';
    }
    if (room.webLink) {
        if (isNextDay(begin)) {
            return `Virtueller Lernraum ${room.short}`;
        }
        return <VirtualMeetingRoomLink roomNumber={room.short} link={room.webLink} begin={begin} end={end} />;
    }
    return room.short;
};

const formatLocation = (location) => {
    return [
        `${location.streetLine1} ${location.streetNumber}`,
        `${location.streetLine2}`,
        `${location.zipCode} ${location.city}`
    ].map(value => value.trim()).filter(value => !!value).join(', ');
};

const detectHybridEventsMissingPresenceLocation = (schedule, locations) => {
    locations = Object.values(locations);
    if (locations.length <= 1) {
        return false;
    }
    if (locations.filter(location => location.title === 'DRV Bund Lernwelt').length === 0) {
        return false;
    }
    return schedule.length === schedule.filter((scheduleItem) => {
        return scheduleItem.slots.length === scheduleItem.slots.filter((slot) => {
            return slot.rooms.length === slot.rooms.filter((room) => !!room.webLink).length && slot.rooms.length > 0;
        }).length && scheduleItem.slots.length > 0;
    }).length && schedule.length > 0;
};

export const CourseTable = ({ courseData }) => {
    const location = courseData.locations[courseData.defaultLocation];
    const schedule = [];
    const addPresenceLocationSlots = detectHybridEventsMissingPresenceLocation(courseData.schedule, courseData.locations);

    for (const scheduleItem of courseData.schedule) {
        const item = {
            date: scheduleItem.date,
            slots: [],
        };
        for (const slot of scheduleItem.slots) {
            const addTimeStringToDate = (date, timeString) => {
                date.setHours(timeString.substring(0, 2), timeString.substring(2, 4), timeString.substring(4, 6));
                return date;
            };
            const begin = addTimeStringToDate(new Date(scheduleItem.date), slot.begin);
            const end = addTimeStringToDate(new Date(scheduleItem.date), slot.end);
            if (addPresenceLocationSlots) {
                item.slots.push({
                    begin,
                    end,
                    location,
                    room: slot.room ?? null,
                });
            } else if (slot.rooms.length === 0) {
                item.slots.push({
                    begin,
                    end,
                    location,
                    room: null,
                });
                continue;
            }
            for (const room of slot.rooms) {
                const roomLocation = courseData.locations[room.locationId];
                item.slots.push({
                    begin,
                    end,
                    location: roomLocation,
                    room: room,
                });
            }
        }
        schedule.push(item);
    }

    return (
        <>
            <Box sx={{ display: 'grid', }}>
                <Box
                    sx={{
                        background: '#EBEFF4',
                        display: 'grid',
                        '@container course-info (max-width: 768px)': {
                            gridTemplateAreas: `"infoText"`,
                            gridTemplateColumns: '100%',
                        },
                        gridTemplateAreas: `"date time location room"`,
                        gridTemplateColumns: '2fr 2fr 5fr 3fr',
                }}>
                    <ScheduleHeadlineGridContainer gridArea='infoText' text='Ablaufplan' />
                    <ScheduleInfoGridContainer gridArea='date'>
                        <MainContentIcon identifier='date' color='#083163' name='Datum' size='3rem' />
                    </ScheduleInfoGridContainer>
                    <ScheduleInfoGridContainer gridArea='time'>
                        <MainContentIcon identifier='time' color='#083163' name='Zeit' size='3rem' />
                    </ScheduleInfoGridContainer>
                    <ScheduleInfoGridContainer gridArea='location'>
                        <MainContentIcon identifier='location' color='#083163' name='Ort' size='3rem' />
                    </ScheduleInfoGridContainer>
                    <ScheduleInfoGridContainer gridArea='room'>
                        <MainContentIcon identifier='room' color='#083163' name='Raum' size='3rem' />
                    </ScheduleInfoGridContainer>
                </Box>
                <Box>
                    {schedule.map((scheduleItem) => {
                        let previousTimespan = '';
                        return scheduleItem.slots.map((slot, index) => {
                            const timespan = formatTime(slot.begin) + formatTime(slot.end);
                            const displayTimespan = previousTimespan !== timespan;
                            previousTimespan = timespan;
                            return (
                                <Fragment key={`scheduleItem-${index}`}>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            '@container course-info (max-width: 768px)': {
                                                gridTemplateColumns: 'min-content max-content',
                                                gridTemplateRows: 'repeat(3, min-content)',
                                                gridTemplateAreas: `
                                                    "dateIcon dateTimeText"
                                                    "locationIcon locationText"
                                                    "roomIcon roomText"
                                                `,
                                                padding: '1rem 0 1rem 0',
                                            },
                                            gridTemplateColumns: '2fr 2fr 5fr 3fr',
                                            gridAutoRows: 'min-content',
                                            gridTemplateAreas: `
                                                "date time location room"
                                            `,
                                            padding: '.5rem 0 .5rem 0',
                                            borderBottom: '1px solid #DADBDC',
                                            '&:last-child': {
                                                border: 'none'
                                            },
                                    }}>
                                        {/* Show icons and mobile components from tablet breakpoint downwards only */}
                                        <ScheduleIconGridContainer gridArea='dateIcon' identifier='date' name='Datum und Uhrzeit' />
                                        <ScheduleItemMobileGridContainer gridArea='dateTimeText'>
                                            <Box sx={{
                                                display: 'grid',
                                                gridTemplateColumns: 'min-content max-content',
                                                gridTemplateRows: 'min-content',
                                                gridTemplateAreas: `
                                                    "dateText timeText"
                                                `,
                                            }}>
                                                <Typography sx={{ display: 'grid', gridArea: 'dateText', fontWeight: 'medium' }}>{formatDate(scheduleItem.date)}</Typography>
                                                <Typography sx={{ display: 'grid', gridArea: 'timeText', pl: '.625rem' }}>{formatTime(slot.begin)}–{formatTime(slot.end)}</Typography>
                                            </Box>
                                        </ScheduleItemMobileGridContainer>

                                        <ScheduleIconGridContainer gridArea='locationIcon' identifier='location' name='Ort' />
                                        <ScheduleItemMobileGridContainer gridArea='locationText'>
                                            <Typography sx={{ fontWeight: 'medium' }}>{slot.location.title}</Typography>
                                            <Typography>{formatLocation(slot.location)}</Typography>
                                        </ScheduleItemMobileGridContainer>

                                        <ScheduleIconGridContainer gridArea='roomIcon' identifier='room' name='Raum' />
                                        <ScheduleItemMobileGridContainer gridArea='roomText'>
                                            <Typography color='#083163'>
                                                <DisplayRoom room={slot.room} begin={slot.begin} end={slot.end} />
                                            </Typography>
                                        </ScheduleItemMobileGridContainer>

                                        {/* Show desktop components from tablet breakpoint upwards only */}
                                        <ScheduleItemDesktopGridContainer gridArea='date'>
                                            {index === 0 && <Typography sx={{ fontWeight: 'medium' }}>{formatDate(scheduleItem.date)}</Typography>}
                                        </ScheduleItemDesktopGridContainer>

                                        <ScheduleItemDesktopGridContainer gridArea='time'>
                                            {displayTimespan && <Typography>{formatTime(slot.begin)}–{formatTime(slot.end)}</Typography>}
                                        </ScheduleItemDesktopGridContainer>

                                        <ScheduleItemDesktopGridContainer gridArea='location'>
                                            <Typography sx={{ fontWeight: 'medium' }}>{slot.location.title}</Typography>
                                            <Typography>{formatLocation(slot.location)}</Typography>
                                        </ScheduleItemDesktopGridContainer>

                                        <ScheduleItemDesktopGridContainer gridArea='room'>
                                            <Typography color='#083163'>
                                                <DisplayRoom room={slot.room} begin={slot.begin} end={slot.end} />
                                            </Typography>
                                        </ScheduleItemDesktopGridContainer>
                                    </Box>
                                </Fragment>
                            );
                        });
                    })}
                </Box>

                {schedule.length === 0 && (
                    <Box
                        sx={{
                            padding: '.5rem 0 .5rem 0',
                            borderBottom: '1px solid #DADBDC',
                            '@container course-info (max-width: 768px)': {
                                padding: '1rem 0 1rem 0',
                            },
                        }}>
                        <Box sx={{ alignSelf: 'center', textAlign: 'center' }}>
                            <Typography>Kein Ablaufplan festgelegt</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
};